/**
 * AdminPasarelas — Configuración de pasarelas de pago por empresa.
 *
 * Solo admin/superadmin. Las llaves NUNCA viajan al frontend en claro;
 * el backend devuelve sólo un masked (****-1234) + flags "tiene_*".
 * Al editar, si el campo se deja vacío se conserva la llave actual; sólo
 * se actualiza si el admin escribe un valor nuevo.
 *
 * Cada tarjeta tiene un botón "Probar conexión" que llama al endpoint de
 * health-check del backend — el resultado se persiste y se muestra en la card.
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, Button, Switch,
  FormControlLabel, Chip, Alert, IconButton, InputAdornment, CircularProgress,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SaveIcon from '@mui/icons-material/Save';
import PaymentIcon from '@mui/icons-material/Payment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useToast from './common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  const activa = localStorage.getItem('empresa_activa');
  if (activa) return activa;
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || '1'; }
  catch { return '1'; }
}
function authHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

// URL del webhook que el cliente debe pegar en su cuenta de la pasarela.
// Es fija — NARA conoce su propio endpoint.
const WEBHOOK_BASE = (process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api').replace(/\/$/, '');

const PASARELAS = [
  {
    key: 'bold',
    nombre: 'BOLD',
    descripcion: 'Tarjetas, PSE, transferencias. Recomendada en Colombia.',
    color: '#7c3aed', emoji: '💳', implementada: true,
    webhookUrl: `${WEBHOOK_BASE}/webhooks/bold`,
    docsUrl: 'https://developers.bold.co/pagos-en-linea/api-link-de-pagos',
  },
  {
    key: 'wompi', nombre: 'Wompi', descripcion: 'Bancolombia. Tarjetas y PSE.',
    color: '#0891b2', emoji: '🟦', implementada: false,
    webhookUrl: `${WEBHOOK_BASE}/webhooks/wompi`,
  },
  {
    key: 'epayco', nombre: 'ePayco', descripcion: 'Multimedios de pago. Colombia.',
    color: '#0d9488', emoji: '🟢', implementada: false,
    webhookUrl: `${WEBHOOK_BASE}/webhooks/epayco`,
  },
];

export default function AdminPasarelas() {
  const empresaId = getEmpresaId();
  const { toast, ToastContainer } = useToast();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/pagos/pasarelas/${empresaId}`, { headers: authHeaders() });
      const d = await r.json();
      setData(d);
    } catch (e) { toast.error('Error cargando pasarelas'); }
    setLoading(false);
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [empresaId]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>
          Pasarelas de Pago
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.5 }}>
          Configura las credenciales de las pasarelas. Las llaves se almacenan cifradas (AES-GCM) y nunca se devuelven al frontend en claro.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
          {PASARELAS.map(p => (
            <PasarelaCard
              key={p.key}
              meta={p}
              config={data[p.key] || {}}
              empresaId={empresaId}
              onSaved={cargar}
              toast={toast}
            />
          ))}
        </Box>
      )}

      <ToastContainer />
    </Box>
  );
}

function PasarelaCard({ meta, config, empresaId, onSaved, toast }) {
  const [editing, setEditing] = useState(false);
  const [activa, setActiva] = useState(!!config.activa);
  const [sandbox, setSandbox] = useState(config.sandbox !== false);
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showApi, setShowApi] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showWh, setShowWh] = useState(false);
  const [extra, setExtra] = useState(JSON.stringify(config.configuracion_extra || {}, null, 2));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setActiva(!!config.activa);
    setSandbox(config.sandbox !== false);
    setExtra(JSON.stringify(config.configuracion_extra || {}, null, 2));
  }, [config]);

  const handleSave = async () => {
    if (!meta.implementada) {
      toast.warning('Esta pasarela aún no está implementada en backend');
      return;
    }
    setSaving(true);
    let extraObj;
    try { extraObj = extra.trim() ? JSON.parse(extra) : {}; }
    catch { toast.error('Configuración extra: JSON inválido'); setSaving(false); return; }

    const body = {
      activa,
      sandbox,
      configuracion_extra: extraObj,
      // Solo enviar campos no-vacíos para no pisar las llaves guardadas
      ...(apiKey ? { api_key: apiKey } : {}),
      ...(secretKey ? { secret_key: secretKey } : {}),
      ...(webhookSecret ? { webhook_secret: webhookSecret } : {}),
    };

    try {
      const r = await fetch(`${API_BASE}/turismo/pagos/pasarelas/${empresaId}/${meta.key}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('Error guardando');
      toast.success(`${meta.nombre} guardada`);
      setApiKey(''); setSecretKey(''); setWebhookSecret('');
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleProbar = async () => {
    if (!meta.implementada) { toast.warning('No implementada'); return; }
    setTesting(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/pagos/pasarelas/${empresaId}/${meta.key}/health-check`, {
        method: 'POST', headers: authHeaders(),
      });
      const d = await r.json();
      if (d.ok) toast.success(`Conexión OK · ${d.mensaje || ''}`);
      else toast.error(`Fallo: ${d.error || 'desconocido'}`);
      onSaved();
    } catch (e) { toast.error(e.message); }
    setTesting(false);
  };

  const fmtFecha = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <Card sx={{ borderRadius: 3, overflow: 'hidden',
                borderTop: `4px solid ${meta.color}`,
                opacity: meta.implementada ? 1 : 0.7 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Typography sx={{ fontSize: 32 }}>{meta.emoji}</Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 800, color: meta.color }}>{meta.nombre}</Typography>
              {config.activa && <Chip label="Activa" size="small" sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 700, height: 20, fontSize: 10 }} />}
              {!meta.implementada && <Chip label="Próximamente" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, height: 20, fontSize: 10 }} />}
            </Box>
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>{meta.descripcion}</Typography>
          </Box>
        </Box>

        {/* Estado actual */}
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            <Indicator label="API key" ok={config.tiene_api_key} />
            <Indicator label="Secret" ok={config.tiene_secret_key} />
            <Indicator label="Webhook" ok={config.tiene_webhook_secret} />
          </Box>
          {config.api_key_masked && (
            <Typography sx={{ fontSize: 10, color: '#64748b', mt: 1, fontFamily: 'monospace' }}>
              API key: {config.api_key_masked}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, fontSize: 11 }}>
            {config.last_health_check_at == null ? (
              <Chip label="Sin probar" size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#f1f5f9', color: '#64748b' }} />
            ) : config.last_health_check_ok ? (
              <Chip icon={<CheckCircleIcon sx={{ fontSize: 12, color: '#16a34a !important' }} />} label={`OK · ${fmtFecha(config.last_health_check_at)}`}
                size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#dcfce7', color: '#15803d' }} />
            ) : (
              <Chip icon={<ErrorIcon sx={{ fontSize: 12, color: '#dc2626 !important' }} />} label={`Falló · ${fmtFecha(config.last_health_check_at)}`}
                size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#fee2e2', color: '#991b1b' }} />
            )}
          </Box>
          {config.last_health_check_error && (
            <Typography sx={{ fontSize: 10, color: '#dc2626', mt: 0.5 }}>{config.last_health_check_error}</Typography>
          )}
        </Box>

        {editing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Información para configurar en la plataforma de la pasarela */}
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <InfoOutlinedIcon sx={{ fontSize: 14, color: '#1d4ed8' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase' }}>
                  Configura esto en tu cuenta de {meta.nombre}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: '#1e3a8a', mb: 0.75 }}>
                Pega esta URL como <strong>Webhook URL</strong> (o "Callback URL") en el panel de {meta.nombre}.
                Cuando un cliente pague, {meta.nombre} notificará a NARA en este endpoint.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.75, bgcolor: '#fff', borderRadius: 1, border: '1px solid #dbeafe' }}>
                <Typography sx={{ flex: 1, fontFamily: 'monospace', fontSize: 11, color: '#0f172a', wordBreak: 'break-all' }}>
                  {meta.webhookUrl}
                </Typography>
                <IconButton size="small" tabIndex={-1}
                  onClick={() => { navigator.clipboard?.writeText(meta.webhookUrl); toast.success('URL copiada'); }}
                  sx={{ color: '#1d4ed8' }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
              {meta.docsUrl && (
                <Typography sx={{ fontSize: 10, color: '#1e3a8a', mt: 0.75 }}>
                  Docs: <a href={meta.docsUrl} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>{meta.docsUrl}</a>
                </Typography>
              )}
            </Box>

            <FormControlLabel
              control={<Switch checked={activa} onChange={e => setActiva(e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Activa</Typography>}
            />
            <FormControlLabel
              control={<Switch checked={sandbox} onChange={e => setSandbox(e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: 13 }}>Modo sandbox (test)</Typography>}
            />

            <SecretField label="API Key" value={apiKey} setValue={setApiKey}
              show={showApi} setShow={setShowApi}
              placeholder={config.tiene_api_key ? 'Dejar vacío = conservar la actual' : 'Pega tu API key de Bold'} />
            <SecretField label="Secret Key" value={secretKey} setValue={setSecretKey}
              show={showSecret} setShow={setShowSecret}
              placeholder={config.tiene_secret_key ? 'Dejar vacío = conservar la actual' : 'Identity key / secret de Bold'} />
            <SecretField label="Webhook Secret" value={webhookSecret} setValue={setWebhookSecret}
              show={showWh} setShow={setShowWh}
              placeholder={config.tiene_webhook_secret ? 'Dejar vacío = conservar el actual' : 'Para firmar webhooks (opcional)'} />

            <TextField label="Configuración extra (JSON)" multiline minRows={2} maxRows={6}
              value={extra} onChange={e => setExtra(e.target.value)} size="small"
              helperText='Ej: {"callback_url": "https://turismo.nara.chat/api/webhooks/bold"}'
              sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 11 } }} />

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button fullWidth size="small" onClick={() => setEditing(false)} disabled={saving}
                sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
              <Button fullWidth size="small" variant="contained" disableElevation
                startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveIcon />}
                onClick={handleSave} disabled={saving || !meta.implementada}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: meta.color, '&:hover': { bgcolor: meta.color, filter: 'brightness(0.9)' } }}>
                Guardar
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button fullWidth size="small" variant="outlined"
              startIcon={<PaymentIcon sx={{ fontSize: 16 }} />}
              onClick={() => setEditing(true)} disabled={!meta.implementada}
              sx={{ textTransform: 'none', borderColor: meta.color, color: meta.color, '&:hover': { borderColor: meta.color, bgcolor: `${meta.color}10` } }}>
              {config.tiene_api_key ? 'Editar credenciales' : 'Configurar'}
            </Button>
            <Button fullWidth size="small" variant="contained" disableElevation
              startIcon={testing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <HealthAndSafetyIcon sx={{ fontSize: 16 }} />}
              onClick={handleProbar} disabled={testing || !config.tiene_api_key || !meta.implementada}
              sx={{ textTransform: 'none', fontWeight: 700, bgcolor: meta.color, '&:hover': { bgcolor: meta.color, filter: 'brightness(0.9)' } }}>
              Probar conexión
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function Indicator({ label, ok }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ok ? '#16a34a' : '#cbd5e1' }} />
      <Typography sx={{ fontSize: 10, color: ok ? '#15803d' : '#94a3b8', fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
}

function SecretField({ label, value, setValue, show, setShow, placeholder }) {
  return (
    <TextField label={label} value={value} onChange={e => setValue(e.target.value)}
      size="small" fullWidth placeholder={placeholder}
      type={show ? 'text' : 'password'}
      autoComplete="off"
      inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShow(!show)} tabIndex={-1}>
              {show ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{ '& input': { fontFamily: 'monospace', fontSize: 12 } }} />
  );
}
